<script setup>
// SiteUp (F2): "Regras de roteamento" — troca automática do assistente de IA por
// gatilho. Quando uma ETIQUETA é aplicada na conversa OU o card entra numa ETAPA
// do Kanban, o assistente que atende muda sem alterar o modo/pausa atual da IA.
// As regras valem para a CONTA toda — opcionalmente restritas a uma caixa de
// entrada. CRUD direto em captain/routing_rules.
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import RoutingRulesAPI from 'dashboard/api/captain/routingRules';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

const route = useRoute();
const store = useStore();
const { isAdmin } = useAdmin();

const assistantId = computed(() => Number(route.params.assistantId));
const canEdit = computed(() => isAdmin.value);

const assistants = useMapGetter('captainAssistants/getRecords');
const labels = useMapGetter('labels/getLabels');
const funnels = useMapGetter('funnel/getFunnels');
const inboxes = useMapGetter('inboxes/getInboxes');

const rules = ref([]);
const isFetching = ref(false);

const TRIGGER_TYPES = [
  { value: 'label', label: 'Etiqueta aplicada' },
  { value: 'kanban_stage', label: 'Etapa do Kanban' },
];
const COPY = {
  routingBehavior:
    'Estas regras valem para a conta toda (qualquer assistente), não só para este. Ao assumir, o assistente de destino lê o histórico da conversa sem alterar o modo atual nem pausar ou reativar a IA. A primeira regra que casar, na ordem da lista, é aplicada.',
  singleAssistant:
    'Você tem só um assistente nesta conta. Crie um segundo assistente (ex.: um para vendas e outro para suporte) para o roteamento fazer sentido.',
  when: 'Quando',
  stage: 'Etapa',
  label: 'Etiqueta',
  select: 'Selecione…',
  inbox: 'Caixa de entrada',
  allInboxes: 'Todas as caixas',
  targetAssistant: 'Trocar para o assistente',
  active: 'Ativa',
  empty: 'Nenhuma regra ainda. Clique em "Adicionar regra" para começar.',
};
const backUrl = computed(() => ({
  name: 'captain_assistants_settings_index',
  params: { accountId: route.params.accountId, assistantId: assistantId.value },
}));

const assistantOptions = computed(() =>
  (assistants.value || []).map(a => ({ value: a.id, label: a.name }))
);
const labelOptions = computed(() =>
  (labels.value || []).map(l => ({ value: l.title, label: l.title }))
);
const inboxOptions = computed(() =>
  (inboxes.value || []).map(i => ({ value: i.id, label: i.name }))
);
// trigger_value de etapa = "<funnel_id>:<stage_key>" (igual ao KanbanItem).
const stageOptions = computed(() => {
  const out = [];
  (funnels.value || []).forEach(funnel => {
    Object.entries(funnel.stages || {})
      .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
      .forEach(([key, stage]) => {
        out.push({
          value: `${funnel.id}:${key}`,
          label: `${funnel.name} › ${stage.name || key}`,
        });
      });
  });
  return out;
});

const triggerValueOptions = rule =>
  rule.trigger_type === 'kanban_stage'
    ? stageOptions.value
    : labelOptions.value;

const blankRule = () => ({
  id: null,
  trigger_type: 'label',
  trigger_value: '',
  inbox_id: '',
  target_assistant_id: '',
  enabled: true,
  isSaving: false,
});

// Normaliza a regra vinda da API (inbox_id null → '' pro select "Todas").
const adopt = (rule, data) =>
  Object.assign(rule, data, { inbox_id: data.inbox_id ?? '', isSaving: false });

const fetchRules = async () => {
  isFetching.value = true;
  try {
    const { data } = await RoutingRulesAPI.get();
    rules.value = (data || []).map(r => ({
      ...r,
      inbox_id: r.inbox_id ?? '',
      isSaving: false,
    }));
  } catch {
    useAlert('Não foi possível carregar as regras.');
  } finally {
    isFetching.value = false;
  }
};

const addRule = () => rules.value.push(blankRule());

const onTriggerTypeChange = rule => {
  rule.trigger_value = '';
};

const payloadFor = rule => ({
  trigger_type: rule.trigger_type,
  trigger_value: rule.trigger_value,
  inbox_id: rule.inbox_id === '' ? null : Number(rule.inbox_id),
  target_assistant_id: Number(rule.target_assistant_id),
  enabled: rule.enabled,
});

const saveRule = async rule => {
  if (!rule.trigger_value) {
    useAlert('Escolha o valor do gatilho (etiqueta ou etapa).');
    return;
  }
  if (!rule.target_assistant_id) {
    useAlert('Escolha o assistente de destino.');
    return;
  }
  rule.isSaving = true;
  try {
    const body = payloadFor(rule);
    const { data } = rule.id
      ? await RoutingRulesAPI.update(rule.id, body)
      : await RoutingRulesAPI.create(body);
    adopt(rule, data);
    useAlert('Regra salva.');
  } catch {
    useAlert('Não foi possível salvar a regra.');
  } finally {
    rule.isSaving = false;
  }
};

const removeRule = async (rule, index) => {
  if (!rule.id) {
    rules.value.splice(index, 1);
    return;
  }
  rule.isSaving = true;
  try {
    await RoutingRulesAPI.delete(rule.id);
    rules.value.splice(index, 1);
    useAlert('Regra removida.');
  } catch {
    rule.isSaving = false;
    useAlert('Não foi possível remover a regra.');
  }
};

const fieldClass =
  'w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm disabled:opacity-60';

onMounted(async () => {
  await Promise.all([
    store.dispatch('captainAssistants/get').catch(() => {}),
    store.dispatch('labels/get').catch(() => {}),
    store.dispatch('funnel/fetch').catch(() => {}),
    store.dispatch('inboxes/get').catch(() => {}),
  ]);
  await fetchRules();
});
</script>

<template>
  <PageLayout
    header-title="Regras de roteamento"
    :is-fetching="isFetching"
    :back-url="backUrl"
    :show-know-more="false"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <SettingsHeader
        heading="Regras de roteamento"
        description="Troque o assistente de IA automaticamente quando uma etiqueta é aplicada na conversa ou o card entra numa etapa do Kanban. Útil para um agente generalista encaminhar a conversa para o especialista de vendas ou suporte."
      />

      <div
        class="flex items-start gap-2 mt-4 px-3 py-2 rounded-xl bg-n-slate-2 text-n-slate-11 text-xs"
      >
        <span class="i-ri-information-line size-4 shrink-0 mt-0.5" />
        <span>{{ COPY.routingBehavior }}</span>
      </div>

      <div
        v-if="assistantOptions.length < 2"
        class="flex items-start gap-2 mt-3 px-3 py-2 rounded-xl bg-n-amber-2 text-n-amber-11 text-xs"
      >
        <span class="i-ri-alert-line size-4 shrink-0 mt-0.5" />
        <span>{{ COPY.singleAssistant }}</span>
      </div>

      <div class="flex flex-col gap-3 mt-6">
        <div
          v-for="(rule, index) in rules"
          :key="rule.id || `new-${index}`"
          class="rounded-xl border border-n-weak p-4 flex flex-col gap-4"
          :class="{ 'opacity-60': rule.isSaving }"
        >
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ COPY.when }}
              </span>
              <select
                v-model="rule.trigger_type"
                :disabled="!canEdit"
                :class="fieldClass"
                @change="onTriggerTypeChange(rule)"
              >
                <option
                  v-for="t in TRIGGER_TYPES"
                  :key="t.value"
                  :value="t.value"
                >
                  {{ t.label }}
                </option>
              </select>
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{
                  rule.trigger_type === 'kanban_stage' ? COPY.stage : COPY.label
                }}
              </span>
              <select
                v-model="rule.trigger_value"
                :disabled="!canEdit"
                :class="fieldClass"
              >
                <option value="">{{ COPY.select }}</option>
                <option
                  v-for="opt in triggerValueOptions(rule)"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ COPY.inbox }}
              </span>
              <select
                v-model="rule.inbox_id"
                :disabled="!canEdit"
                :class="fieldClass"
              >
                <option value="">{{ COPY.allInboxes }}</option>
                <option
                  v-for="opt in inboxOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ COPY.targetAssistant }}
              </span>
              <select
                v-model="rule.target_assistant_id"
                :disabled="!canEdit"
                :class="fieldClass"
              >
                <option value="">{{ COPY.select }}</option>
                <option
                  v-for="opt in assistantOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </label>
          </div>

          <div class="flex items-center justify-between gap-3 flex-wrap">
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input
                v-model="rule.enabled"
                type="checkbox"
                :disabled="!canEdit"
              />
              {{ COPY.active }}
            </label>

            <div class="flex items-center gap-2">
              <Button
                label="Salvar"
                icon="i-ri-check-line"
                sm
                :is-loading="rule.isSaving"
                :disabled="!canEdit"
                @click="saveRule(rule)"
              />
              <Button
                icon="i-ri-delete-bin-line"
                slate
                ghost
                sm
                :disabled="!canEdit || rule.isSaving"
                @click="removeRule(rule, index)"
              />
            </div>
          </div>
        </div>

        <div v-if="!rules.length" class="text-sm text-n-slate-11">
          {{ COPY.empty }}
        </div>

        <div>
          <Button
            label="Adicionar regra"
            icon="i-ri-add-line"
            slate
            faded
            sm
            :disabled="!canEdit"
            @click="addRule"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
